import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Text } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { renderTextWithFootnotesReferencesV2, isCarlineData } from '@oneaudi/feature-app-utils';
import { Context } from '../../Context';
import type { AppearanceProps } from '../../../types';

import type { I18nTexts } from '../../utils/fetchi18n';
import { fetchI18n } from '../../utils/fetchi18n';
import { ContentContext } from '../../ContentContext';
import { useDataFromOneGraph } from '../../hooks/useDataFromOneGraph';

export const PriceStyledContainer = styled.div`
  width: auto;
  pointer-events: auto;
  padding-top: var(--spacing-relative-xl)};
`;

interface StagePricesWrap {
  appearance?: AppearanceProps;
}
const StagePricesWrap = styled.div<StagePricesWrap>``;

const StagePrices: React.FC = () => {
  const { i18nService, loggerService: logger, numberFormatterService } = useContext(Context);
  const [i18nTexts, setI18nTexts] = useState<I18nTexts | null>(null);
  const { content } = React.useContext(ContentContext);
  const { prices: pricesContent, priceFootnotes, consumptionAndEmissions } = content;
  const { totalPrice: showTotalPrice } = pricesContent;

  // we only want to fetch the price for the first entry as this will has to be the carline
  const oneGraphData = useDataFromOneGraph(consumptionAndEmissions && consumptionAndEmissions[0]);
  useEffect(() => {
    // no need to fetch i18n texts more than once
    if (i18nTexts) return;
    // Create an async function to fetch data
    const getI18nTexts = async () => {
      try {
        // @ts-expect-error logger type may be undefined
        const data = await fetchI18n({ i18nService, logger });
        setI18nTexts(data);
      } catch (error) {
        logger?.error('Error fetching i18n texts:', error);
      }
    };

    // Call the async function and catch any unhandled errors
    getI18nTexts().catch((error) => {
      logger?.error('Unhandled error:', error);
    });
  }, []);

  const prices = useMemo(() => {
    const isCarline = isCarlineData(oneGraphData); // Check if the data is carline
    return isCarline && oneGraphData?.carline?.prices; // Return prices if it's carline data
  }, [oneGraphData]);

  let totalPrice;

  if (numberFormatterService?.formatCurrency) {
    if (prices && prices?.minPrice?.value && showTotalPrice) {
      const formattedPrice = numberFormatterService
        .formatCurrency(prices.minPrice.value.toString())
        .toString();
      // Format footnotes (default to an empty string if no footnotes exist)
      const formattedFootnotes =
        priceFootnotes?.map((footnote) => `{ft_${footnote}}`).join(', ') || '';
      const totalPriceText = i18nTexts?.totalPriceText.replace(
        '${value}',
        `${formattedPrice}${formattedFootnotes}`,
      );
      totalPrice = totalPriceText && renderTextWithFootnotesReferencesV2(totalPriceText);
    }
  }

  return (
    <PriceStyledContainer data-testid="stage-prices">
      <Text variant={{ order: '1', type: 'copy', style: 'normal' }}>{totalPrice}</Text>
    </PriceStyledContainer>
  );
};
export default StagePrices;
